import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Typography } from '@/components';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ASSETS_BASE_URL } from '@/config';
import { useNotifications } from '@/features/notifications';
import { toAbsoluteUrl } from '@/helpers';
import { useCountries } from '@/hooks';

import { useUpdateCompanyMutation } from '../../api';
import { CompanySchema } from '../../types';
import { ProfileHeaderDrawer } from './profile-header-drawer';
import { ProfileHeaderFormFields } from './profile-header-form';

const FORM_ID = 'company-profile-header-form';

export type ProfileHeaderCardProps = {
  readonly company: CompanySchema;
};

export function ProfileHeaderCard({ company }: ProfileHeaderCardProps) {
  const { t } = useTranslation(['settings']);
  const [editing, setEditing] = useState(false);
  const [updateCompany, { isLoading }] = useUpdateCompanyMutation();
  const { add } = useNotifications();
  const { map: mapCountry } = useCountries();

  const onSubmit = (data: ProfileHeaderFormFields) => {
    updateCompany({
      id: company.id,
      data: {
        name: data.name,
        website: data.website,
        linkedin: data.linkedin,
        logo: data.logo,
        short_description: data.short_description,
        hq_country: data.hq_country,
      },
    })
      .unwrap()
      .then(() => {
        add({
          id: new Date().toString(),
          title: t('Company updated'),
          description: t('Your company has been updated'),
        });
        setEditing(false);
      })
      .catch(() => {
        add({
          id: new Date().toString(),
          title: t('ERROR'),
          description: t('There was an error updating your company. Please try again later.'),
        });
      });
  };

  const initials = (company.name ?? '?').trim().slice(0, 2).toUpperCase();
  const hqLabel = company.hq_country ? (mapCountry(company.hq_country) ?? company.hq_country) : '';
  const websiteLabel = company.website ? company.website.replace(/^https?:\/\//, '') : '';
  const hasDetails = Boolean(
    company.logo || company.hq_country || company.website || company.linkedin || company.short_description
  );

  return (
    <>
      <Card className="p-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:gap-6">
          <div className="bg-primary-900 flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl text-white">
            {company.logo ? (
              <img src={`${ASSETS_BASE_URL}/${company.logo}`} alt={company.name} className="h-full w-full object-cover" />
            ) : (
              <Typography as="span" size="title" color="white" weight="semibold">
                {initials}
              </Typography>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <Typography as="h2" size="title" color="primary" weight="semibold">
                  {company.name}
                </Typography>
                {company.verified ? (
                  <Badge variant="outline" className="gap-1 rounded-full border-transparent bg-primary-100 text-primary-800 font-semibold">
                    <Icon name="check" className="h-3.5 w-3.5" />
                    {t('Verified')}
                  </Badge>
                ) : null}
              </div>

              <Button size="sm" color="neutral" icon="edit" onClick={() => setEditing(true)}>
                {t('Edit')}
              </Button>
            </div>

            {hasDetails ? (
              <>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-neutral-500">
                  {hqLabel ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="map-pin" className="h-4 w-4" />
                      {hqLabel}
                    </span>
                  ) : null}
                  {company.website ? (
                    <a
                      className="text-primary-700 inline-flex items-center gap-1.5"
                      href={toAbsoluteUrl(company.website) ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon name="web" className="h-4 w-4" />
                      {websiteLabel}
                    </a>
                  ) : null}
                </div>

                {company.linkedin ? (
                  <a
                    className="text-primary-700 mt-3 inline-flex items-center gap-1.5 font-semibold"
                    href={toAbsoluteUrl(company.linkedin) ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="linkedin" className="h-4 w-4" />
                    LinkedIn
                  </a>
                ) : null}

                {company.short_description ? (
                  <Typography as="p" size="body-2" color="primary" className="mt-3 max-w-2xl">
                    {company.short_description}
                  </Typography>
                ) : null}
              </>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-primary-700 mt-3 block text-left text-base font-medium hover:underline"
              >
                {t('Complete your company profile here')}
              </button>
            )}
          </div>
        </div>
      </Card>

      <ProfileHeaderDrawer
        formId={FORM_ID}
        company={company}
        isOpen={editing}
        isLoading={isLoading}
        onSubmit={onSubmit}
        onClose={() => setEditing(false)}
      />
    </>
  );
}
